import { FC, useEffect } from "react";
import { Link, useMatch } from "react-location";
import { OfficeButton, OfficeHeader, Show } from "components";
import { classNames, useUrlState } from "utils";

import AddIntermediariesContainer from "./add-intermediaries";
import AddParticipantsContainer from "./add-participants";
import AddQuarterContainer from "./add-quarter";
import CreateAdjustmentStatementContainer from "./add-adjustment-statement";
import { LocationGenerics } from "router/location";
import ModifyDeductionContainer from "./modify-deductions";
import NonProportionalReceivablePayments from "./tabs/non-proportional-receivable-payments";
import Payments from "./payments";
import ProfitStatementCommisionContainer from "./add-profit-statement-commsion";
import TreatyBrokers from "./tabs/intermediaries";
import TreatyBrokersAssociates from "./tabs/intermediaries-associates";
import TreatyEndorsementsTab from "./tabs/endorsements";
import TreatyOverview from "./overview";
import TreatyReinsurers from "./tabs/reinsurers";
import TreatyReinsurersAssociates from "./tabs/reinsurers-associates";
import { TreatyType } from "_graphql/__generated__/globalTypes";
import { useAppNavigate } from "_graphql/cache/auth";
import { useTreaty } from "hooks/data/use-treaties";

type Props = {};

const tabs = [
  { name: "Reinsurers", href: "participants" },
  { name: "Reinsurer Associates", href: "reinsurers" },
  { name: "Intermediaries ", href: "brokers" },
  { name: "Intermediaries's Associates", href: "associates" },
  { name: "Endorsements", href: "endorsements" },
];
const payments = (type?: TreatyType) => [
  { name: "Payments", href: "payments" },
  ...(type === TreatyType.NONPROPORTIONAL
    ? [{ name: "Receivable Payments", href: "receivables" }]
    : []),
];

const ViewTreatyPage: FC<Props> = (props) => {
  const navigate = useAppNavigate();
  const { params } = useMatch<LocationGenerics>();
  const [modal, setModal] = useUrlState("modal");
  const { treaty } = useTreaty({
    treatyId: params?.treaty,
  });
  const [activeTab] = useUrlState("activeTab");
  const isProportional =
    treaty?.treaty_program?.treaty_type === TreatyType.PROPORTIONAL;
  const allQuartersFiled = treaty?.treaty_accounts?.length === 4;
  const isSpecialProgram = /fire|general accident/g.test(
    treaty?.treaty_program?.treaty_name?.toLowerCase() || ""
  );
  const allowQuaerterlyFiling =
    (treaty?.treaty_participants &&
      treaty?.treaty_participants?.length > 0 &&
      treaty?.treaty_participants?.every(
        (participant) => participant?.treaty_participation_percentage
      )) ||
    false;

  useEffect(() => {
    if (!treaty) return;
    if (treaty?.treaty_program?.treaty_type === TreatyType.NONPROPORTIONAL) {
      navigate({
        search(prev) {
          return {
            ...prev,
            layer: "1",
          };
        },
      });
    }
  }, [treaty]);

  return (
    <main className="flex-1 flex flex-col overflow-hidden bg-background-500">
      <OfficeHeader
        renderActions={() => (
          <>
            <OfficeButton onClick={() => setModal("create-reinsurers")}>
              <span className="inline text-xs sm:hidden">Reinsurers</span>
              <span className="hidden sm:inline">Create Reinsurers List</span>
            </OfficeButton>
            <OfficeButton onClick={() => setModal("create-intermediaries")}>
              <span className="inline text-xs sm:hidden">Intermediaries</span>
              <span className="hidden sm:inline">Create Intermediary List</span>
            </OfficeButton>
            <Show
              if={allowQuaerterlyFiling && isProportional && !allQuartersFiled}
            >
              <OfficeButton onClick={() => setModal("add-quarter")}>
                <span className="inline text-xs sm:hidden">Add Quarter</span>
                <span className="hidden sm:inline">
                  File Quarterly Statement
                </span>
              </OfficeButton>
            </Show>
            <Show if={allQuartersFiled && isProportional}>
              <OfficeButton onClick={() => setModal("portfolio-statement")}>
                <span className="inline text-xs sm:hidden">Profit</span>
                <span className="hidden sm:inline">
                  {isSpecialProgram
                    ? "Profit Comm. & Portfolio Stmt"
                    : "Profit Comm."}
                </span>
              </OfficeButton>
            </Show>
          </>
        )}
      />
      <div className="block">
        <div className="border-b border-background-500 bg-card-500 px-4 sm:px-6">
          <nav
            className="-mb-px flex items-center space-x-4 sm:space-x-8 overflow-x-auto no-scrollbar whitespace-nowrap"
            aria-label="Tabs"
          >
            <Link
              search={(old) => ({
                ...old,
                activeTab: undefined,
              })}
              className={classNames(
                !activeTab
                  ? "border-gray-200 text-tertiary"
                  : "border-transparent text-primary hover:text-gray-300 hover:border-gray-200",
                "shrink-0 flex py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm"
              )}
              aria-current={!activeTab ? "page" : undefined}
            >
              Overview
            </Link>
            {tabs.map((_activeTab) => (
              <Link
                key={_activeTab.name}
                search={(old) => ({
                  ...old,
                  activeTab: _activeTab.href,
                })}
                className={classNames(
                  activeTab === _activeTab.href
                    ? "border-gray-200 text-tertiary"
                    : "border-transparent text-primary hover:text-gray-300 hover:border-gray-200",
                  "shrink-0 flex py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm"
                )}
                aria-current={
                  activeTab === _activeTab.href ? "page" : undefined
                }
              >
                {_activeTab.name}
              </Link>
            ))}

            <div className="h-6 w-px bg-gray-300" />
            {payments(
              treaty?.treaty_program?.treaty_type || TreatyType.PROPORTIONAL
            ).map((_activeTab) => (
              <Link
                key={_activeTab.name}
                search={(old) => ({
                  ...old,
                  activeTab: _activeTab.href,
                })}
                className={classNames(
                  activeTab === _activeTab.href
                    ? "border-gray-200 text-tertiary"
                    : "border-transparent text-primary hover:text-gray-300 hover:border-gray-200",
                  "shrink-0 flex py-3 sm:py-4 px-2 sm:px-1 border-b-2 font-medium text-xs sm:text-sm"
                )}
                aria-current={
                  activeTab === _activeTab.href ? "page" : undefined
                }
              >
                {_activeTab.name}
              </Link>
            ))}
          </nav>
        </div>
      </div>
      <div className="flex flex-1 overflow-y-auto">
        <div className="flex-1 mx-auto min-w-0 overflow-hidden overflow-y-auto light flex">
          {!activeTab && (
            <TreatyOverview allowQuarterlyFiling={allowQuaerterlyFiling} />
          )}
          {activeTab === "brokers" && <TreatyBrokers />}
          {activeTab === "participants" && <TreatyReinsurers />}
          {activeTab === "reinsurers" && <TreatyReinsurersAssociates />}
          {activeTab === "associates" && <TreatyBrokersAssociates />}
          {activeTab === "endorsements" && <TreatyEndorsementsTab />}
          {activeTab === "payments" && treaty?.treaty_program?.treaty_type && (
            <Payments treaty_type={treaty?.treaty_program?.treaty_type || ""} />
          )}
          {activeTab === "receivables" && <NonProportionalReceivablePayments />}
        </div>
      </div>

      {modal && (
        <>
          <AddParticipantsContainer
            open={modal === "create-reinsurers"}
            setOpen={(val: boolean) =>
              setModal(val ? "create-reinsurers" : undefined)
            }
          />
          <AddIntermediariesContainer
            open={modal === "create-intermediaries"}
            setOpen={(val: boolean) =>
              setModal(val ? "create-intermediaries" : undefined)
            }
          />
          <ModifyDeductionContainer
            open={modal === "modify-deduction"}
            setOpen={(val: boolean) =>
              setModal(val ? "create-intermediaries" : undefined)
            }
          />
          <AddQuarterContainer
            open={modal === "add-quarter"}
            setOpen={(val: boolean) => {
              setModal(val ? "add-quarter" : undefined);
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    // modal: undefined,
                    id: undefined,
                  };
                },
              });
            }}
          />
          <CreateAdjustmentStatementContainer
            open={
              modal === "add-adjustment-statement" ||
              modal === "update-adjustment-statement"
            }
            setOpen={(val: boolean) => {
              setModal(val ? modal : undefined);
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    // modal: undefined,
                    id: undefined,
                  };
                },
              });
            }}
          />
          <ProfitStatementCommisionContainer
            open={modal === "portfolio-statement"}
            setOpen={(val: boolean) => {
              // setModal(val ? "portfolio-statement" : undefined);
              navigate({
                search(prev) {
                  return {
                    ...prev,
                    modal: val ? prev?.modal : undefined,
                    id: undefined,
                  };
                },
              });
            }}
          />
        </>
      )}
    </main>
  );
};

export default ViewTreatyPage;
